import { useMemo, type ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { Button } from "../components/button";
import { type Colors, useColors } from "../theme";

interface Props {
  /** Coral-bordered when true — used for the primary "Unlock" card. */
  primary?: boolean;
  eyebrow: string;
  /** Big price string. Pass with embedded unit ("/mo") via children. */
  price: string;
  /** Inline unit suffix shown smaller after the price (e.g. "/mo"). */
  unit?: string;
  description: string;
  /** Optional green-✓ feature list (web/wide paywall, design_handoff_2 §6). */
  features?: string[];
  ctaLabel: string;
  ctaVariant?: "coral" | "soft";
  onCtaPress: () => void;
  disabled?: boolean;
  /** Optional tertiary CTA below the main button (e.g. annual link). */
  belowCta?: ReactNode;
  testID?: string;
}

// Reusable pricing card — one-time, subscribe, and any future tier
// fit the same shape. The primary card gets the 2px coral border;
// secondary tiers get the soft button variant.
export function PlanCard({
  primary,
  eyebrow,
  price,
  unit,
  description,
  features,
  ctaLabel,
  ctaVariant = "coral",
  onCtaPress,
  disabled,
  belowCta,
  testID,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <View style={[styles.plan, primary && styles.planPrimary]}>
      <Text style={styles.eyebrow}>{eyebrow}</Text>
      <Text style={styles.price}>
        {price}
        {unit ? <Text style={styles.priceUnit}> {unit}</Text> : null}
      </Text>
      <Text style={styles.description}>{description}</Text>
      {features && features.length > 0 ? (
        <View style={styles.featureList}>
          {features.map((f) => (
            <View key={f} style={styles.featureRow}>
              <Text style={styles.featureCheck}>✓</Text>
              <Text style={styles.featureText}>{f}</Text>
            </View>
          ))}
        </View>
      ) : null}
      <Button
        label={ctaLabel}
        onPress={onCtaPress}
        disabled={disabled}
        fullWidth
        variant={ctaVariant === "coral" ? "primary" : "secondary"}
        testID={testID}
      />
      {belowCta}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    plan: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 16,
      padding: 18,
      backgroundColor: colors.surface,
      marginBottom: space[3],
    },
    planPrimary: {
      borderColor: colors.action,
      borderWidth: 2,
      padding: 17,
    },
    eyebrow: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 11,
      letterSpacing: 0.6,
      color: colors.textMuted,
      textTransform: "uppercase",
    },
    price: {
      fontFamily: fontFamily.extraBold,
      fontSize: 34,
      letterSpacing: -0.5,
      color: colors.heading,
      marginTop: 4,
      marginBottom: 2,
    },
    priceUnit: {
      fontFamily: fontFamily.semiBold,
      fontSize: 15,
      color: colors.textMuted,
    },
    description: {
      ...textStyle.bodySmall,
      color: colors.textSecondary,
      marginBottom: space[4],
      lineHeight: 21,
    },
    featureList: { gap: 8, marginBottom: space[4] },
    featureRow: { flexDirection: "row", gap: 9, alignItems: "flex-start" },
    featureCheck: {
      fontFamily: fontFamily.bold,
      fontSize: 14,
      color: colors.success,
      lineHeight: 20,
    },
    featureText: {
      flex: 1,
      ...textStyle.bodySmall,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });
}
